import type { Command } from 'commander';
import { updateAggregate, type OrchestrationResult } from '../../../lib/resume-orchestrator';
import type { PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, printTable, sanitizeForTerminal } from '../../../lib/output';

interface UpdateFlags {
	file: string;
	section?: string;
}

/** 同 view.ts 慣例：檔案小而聚焦，各檔各自複製一份，不額外拉一個共用模組。 */
function requireEncId(encId: string): string {
	const trimmed = encId.trim();
	if (!trimmed) {
		throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	}
	return trimmed;
}

/**
 * `resumes update` 核心：無本地整份 `validateAggregate` 前置驗證 —— `--section` 模式下輸入
 * 檔可能只含單節內容，套用整份聚合驗證器（要求頂層 `name` 必填）會誤判；交給 server 422
 * 把關，partial success 記入逐節報告（見 resume-orchestrator.ts 的 updateAggregate 註解）。
 * 結果先印出（json/table），再視 `allOk` 決定是否丟 CliError 觸發非 0 exit（同 create.ts／
 * `jobs batch` 慣例）。
 */
export async function runResumesUpdate(
	ctx: ResolvedContext,
	encId: string,
	filePath: string,
	flags: UpdateFlags
): Promise<void> {
	const trimmed = requireEncId(encId);
	const input = readJsonObject(filePath, { timeoutMs: ctx.timeoutMs });
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const result = await updateAggregate(opts, trimmed, input, flags.section);
	printOrchestrationResult(ctx, result);

	if (!result.allOk) {
		const failed = result.reports.filter((r) => !r.ok).map((r) => r.section);
		throw new CliError(
			`Resume ${result.enc_id} was partially updated — ${failed.length} section(s) failed: ${failed.join(', ')}. ` +
				`Fix the input and retry with --section <name>.`,
			ExitCode.ServerClientError
		);
	}
}

/**
 * create/update 共用的結果呈現，各自複製一份（檔案小而聚焦，同 list.ts formatDate 慣例）：
 * json 印 `{enc_id, reports, all_ok}`；table 印 enc_id 前綴行 + 每節一行 ✓/✗ + error。
 */
function printOrchestrationResult(ctx: ResolvedContext, result: OrchestrationResult): void {
	if (ctx.format === 'json') {
		printJson({ enc_id: result.enc_id, reports: result.reports, all_ok: result.allOk });
		return;
	}
	process.stdout.write(`Resume: ${sanitizeForTerminal(result.enc_id ?? '')}\n`);
	printTable(
		result.reports,
		[
			{ header: 'SECTION', value: (r) => r.section, maxWidth: 20 },
			{ header: 'STATUS', value: (r) => (r.ok ? '✓' : '✗') },
			{ header: 'ERROR', value: (r) => r.error ?? '', maxWidth: 60 },
		],
		ctx.color
	);
}

export function registerPersonalResumesUpdate(parent: Command): void {
	parent
		.command('update <enc_id>')
		.description('Update a resume aggregate (whole file, or one section with --section)')
		.requiredOption('--file <path>', 'path to a JSON resume aggregate (or single-section content), or "-" for stdin')
		.option('--section <name>', 'only write this section (e.g. education, work_experience)')
		.action(async (encId: string, flags: UpdateFlags, command: Command) => {
			await runResumesUpdate(resolveContext(command), encId, flags.file, flags);
		});
}
